import React, { useEffect, useMemo, useRef, useState } from "react"
import { FaSearch, FaSyncAlt, FaChevronDown, FaTimes, FaSpinner, FaFileExcel } from "react-icons/fa"
import { FiX } from "react-icons/fi"
import { LuEye, LuEyeOff } from "react-icons/lu"
import {
  FilterOption,
  HOLDER_LABELS,
  HolderType,
  STATUS_BADGE,
  SupplierKind,
  materialMasters,
  statusLabel,
} from "./material.api"

/**
 * The pieces every Material Management screen shares: a status badge, a
 * searchable async picker, the scope-aware filter bar, and the table shells.
 *
 * Kept together rather than spread across the pages because six documents
 * with six slightly different filter bars is how a scoped module ends up with
 * one screen that quietly filters differently from the rest.
 */

export const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span
    className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
      STATUS_BADGE[status] || "bg-gray-100 text-gray-700"
    }`}
  >
    {statusLabel(status)}
  </span>
)

export const HolderChip: React.FC<{ type?: HolderType | string | null; label?: string | null; code?: string | null }> = ({
  type,
  label,
  code,
}) => {
  if (!type) return <span className="text-gray-400">-</span>
  const tone =
    type === "warehouse" ? "bg-slate-100 text-slate-700" : type === "staff" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded uppercase ${tone}`}>
        {HOLDER_LABELS[type as HolderType] || type}
      </span>
      <span className="text-gray-900">{label || "-"}</span>
      {code ? <span className="text-xs text-gray-400">({code})</span> : null}
    </span>
  )
}

/**
 * An async picker that loads its options from a fetcher.
 *
 * Every filter dropdown in this module is scoped server-side, so the fetcher
 * is always one of the `materialFilters` calls and the component never has to
 * know what the caller may see. It re-fetches when `dependency` changes, which
 * is what keeps a warehouse list in step with the project filter above it.
 */
export const AsyncPicker: React.FC<{
  value: FilterOption | null
  onChange: (option: FilterOption | null) => void
  fetcher: (search: string) => Promise<FilterOption[]>
  placeholder?: string
  disabled?: boolean
  dependency?: any
  className?: string
  emptyHint?: string
  /** Only search once this many characters are typed (large lists, e.g. vendors). */
  minSearch?: number
}> = ({ value, onChange, fetcher, placeholder = "Select...", disabled, dependency, className = "", emptyHint, minSearch = 0 }) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [options, setOptions] = useState<FilterOption[]>([])
  const [loading, setLoading] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  const tooShort = minSearch > 0 && search.trim().length < minSearch

  useEffect(() => {
    if (!open) return
    if (tooShort) {
      setOptions([])
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    const timer = setTimeout(() => {
      fetcher(search.trim())
        .then((rows) => {
          if (!cancelled) setOptions(rows)
        })
        .catch(() => {
          if (!cancelled) setOptions([])
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, search, dependency]) // eslint-disable-line react-hooks/exhaustive-deps

  // Changing what the picker depends on invalidates the selection: a warehouse
  // from the old project is not an option under the new one.
  const firstRender = useRef(true)
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    onChange(null)
    setOptions([])
  }, [dependency]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onClickOutside)
    return () => document.removeEventListener("mousedown", onClickOutside)
  }, [])

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm border border-gray-300 rounded bg-white disabled:bg-gray-100 disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-800"
      >
        <span className={value ? "text-gray-900 truncate" : "text-gray-400 truncate"}>
          {value ? value.name : placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {value && !disabled ? (
            <FaTimes
              className="text-gray-400 hover:text-gray-700"
              onClick={(e) => {
                e.stopPropagation()
                onChange(null)
              }}
            />
          ) : null}
          <FaChevronDown className="text-gray-400 text-xs" />
        </span>
      </button>

      {open ? (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-800"
              />
            </div>
          </div>
          <div className="overflow-y-auto">
            {tooShort ? (
              <div className="p-4 text-center text-sm text-gray-500">
                Type at least {minSearch} characters to search
              </div>
            ) : loading ? (
              <div className="p-4 text-center text-sm text-gray-500">
                <FaSpinner className="animate-spin inline mr-2" /> Loading...
              </div>
            ) : options.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                {emptyHint || "Nothing to choose from"}
              </div>
            ) : (
              options.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    onChange(option)
                    setOpen(false)
                    setSearch("")
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center justify-between gap-2"
                >
                  <span className="truncate">{option.name}</span>
                  {option.code ? <span className="text-xs text-gray-400 shrink-0">{option.code}</span> : null}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}

/**
 * Supplier picker for requests and arrangements: a Material Supplier, or a
 * vendor from the Vendor master standing in as the supplier. Vendors are a
 * long list, so that dropdown only searches once 3 characters are typed.
 */
export const SupplierPartyPicker: React.FC<{
  kind: SupplierKind
  onKindChange: (kind: SupplierKind) => void
  value: FilterOption | null
  onChange: (option: FilterOption | null) => void
  disabled?: boolean
}> = ({ kind, onKindChange, value, onChange, disabled }) => (
  <div className="space-y-2">
    <div className="inline-flex rounded border border-gray-300 overflow-hidden text-xs">
      {(["supplier", "vendor"] as SupplierKind[]).map((k) => (
        <button
          key={k}
          type="button"
          disabled={disabled}
          onClick={() => {
            if (k === kind) return
            onKindChange(k)
            onChange(null)
          }}
          className={`px-3 py-1 font-medium ${k === kind ? "bg-blue-800 text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
        >
          {k === "supplier" ? "Supplier" : "Vendor"}
        </button>
      ))}
    </div>
    {kind === "vendor" ? (
      <AsyncPicker
        key="vendor"
        value={value}
        onChange={onChange}
        fetcher={(s) => materialMasters.vendors(s)}
        placeholder="Search vendor..."
        minSearch={3}
        emptyHint="No active vendor matches"
        disabled={disabled}
      />
    ) : (
      <AsyncPicker
        key="supplier"
        value={value}
        onChange={onChange}
        fetcher={(s) => materialMasters.suppliers(s)}
        placeholder="Select supplier..."
        disabled={disabled}
      />
    )}
  </div>
)

/**
 * Right-side slide-over panel. Every Material Management form uses this.
 *
 * **z-index matters here.** The sidebar is `z-50` and the header `z-40`, so a
 * backdrop below `z-50` leaves the sidebar sitting on top of it and clickable
 * through the overlay. Both the backdrop and the panel are `z-50`, matching
 * material-master-page.tsx and the other 275 `z-50` overlays in the app; they
 * win against the sidebar because the page content renders after it in the
 * DOM. The confirm/prompt dialog sits above everything at `z-[100]`, which is
 * what lets a confirmation open from inside an open panel.
 *
 * The panel owns the page scroll while it is open, so the list behind cannot
 * scroll away underneath it.
 */
export const OffCanvas: React.FC<{
  onClose: () => void
  title: string
  subtitle?: string
  footer?: React.ReactNode
  /** md = simple forms, lg/xl = forms carrying an item table. */
  width?: "md" | "lg" | "xl"
  children: React.ReactNode
}> = ({ onClose, title, subtitle, footer, width = "xl", children }) => {
  // Mount first, then slide, so the panel animates in rather than appearing.
  const [shown, setShown] = useState(false)

  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true))
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)

    // Lock the page behind the panel. The previous value is restored rather
    // than blanked, so a panel opened over something that was already locked
    // does not unlock it on close.
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener("keydown", onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [onClose])

  const widthClass = width === "md" ? "max-w-2xl" : width === "lg" ? "max-w-3xl" : "max-w-5xl"

  return (
    <>
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-200 ${
          shown ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`fixed top-0 right-0 h-full h-dvh w-full ${widthClass} bg-white shadow-xl z-50 flex flex-col transform transition-transform duration-200 ease-out ${
          shown ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-start justify-between gap-4 p-4 sm:p-5 border-b border-gray-200 bg-gray-50 shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {subtitle ? <p className="text-xs text-gray-600 mt-1">{subtitle}</p> : null}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-2 hover:bg-gray-200 rounded transition-colors shrink-0"
          >
            <FiX className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5 space-y-5">{children}</div>

        {footer ? (
          <div className="border-t border-gray-200 bg-gray-50 px-4 sm:px-5 py-3 sm:py-4 shrink-0">
            <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">{footer}</div>
          </div>
        ) : null}
      </div>
    </>
  )
}

/**
 * The list layout every table screen in the app shares - Billing, Project
 * Sites, the masters: ONE card holding a header strip, a filter strip, the
 * table and the pagination, in that order. The pieces below reproduce those
 * classes exactly so a Material Management list is indistinguishable from the
 * rest of the app; change them together or not at all.
 */
export const ListCard: React.FC<{
  title: string
  subtitle?: React.ReactNode
  icon?: React.ReactNode
  actions?: React.ReactNode
  filters?: React.ReactNode
  /** Pagination, rendered under the table inside the card. */
  footer?: React.ReactNode
  children: React.ReactNode
}> = ({ title, subtitle, icon, actions, filters, footer, children }) => (
  <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
    <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          {icon ? (
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 shrink-0">
              {icon}
            </div>
          ) : null}
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            {subtitle ? <p className="text-sm text-gray-600 max-w-3xl">{subtitle}</p> : null}
          </div>
        </div>
        {actions ? <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">{actions}</div> : null}
      </div>
    </div>

    {filters ? <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">{filters}</div> : null}

    {children}

    {footer}
  </div>
)

/** A row of labelled filter controls, laid out like the Billing filter strip. */
export const FilterRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-col lg:flex-row lg:flex-wrap gap-4 lg:items-end">{children}</div>
)

/** One labelled control in a FilterRow. `grow` for the search box. */
export const FilterField: React.FC<{ label: string; grow?: boolean; className?: string; children: React.ReactNode }> = ({
  label,
  grow,
  className = "",
  children,
}) => (
  <div className={`${grow ? "flex-1 lg:max-w-md" : "w-full lg:w-56"} ${className}`}>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    {children}
  </div>
)

/**
 * One-click filter chips under the filter row - the same treatment as the
 * "Pending Work" chips on Project Sites. Used for workflow status and for the
 * reconciliation views.
 */
export const StatusTabs: React.FC<{
  tabs: Array<{ key: string; label: string }>
  value: string
  onChange: (key: string) => void
  label?: string
}> = ({ tabs, value, onChange, label = "Status" }) => (
  <div className="mt-4 pt-4 border-t border-gray-200">
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-sm font-medium text-gray-700">{label}:</span>
      {tabs.map((tab) => (
        <button
          key={tab.key || "all"}
          type="button"
          onClick={() => onChange(tab.key)}
          aria-pressed={value === tab.key}
          className={`inline-flex items-center px-3 py-1.5 rounded border text-xs font-medium transition-colors ${
            value === tab.key
              ? "border-blue-300 bg-blue-50 text-blue-700"
              : "border-gray-200 bg-white text-gray-700 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700"
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  </div>
)

/** Two or more views of one page (List / Summary on Project Sites). */
export const ViewToggle: React.FC<{
  options: Array<{ key: string; label: string; icon?: React.ReactNode }>
  value: string
  onChange: (key: string) => void
}> = ({ options, value, onChange }) => (
  <div className="flex rounded border border-gray-200 overflow-hidden">
    {options.map((option, index) => (
      <button
        key={option.key}
        type="button"
        onClick={() => onChange(option.key)}
        aria-pressed={value === option.key}
        className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium ${index > 0 ? "border-l border-gray-200" : ""} ${
          value === option.key ? "bg-blue-800 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
        }`}
      >
        {option.icon}
        {option.label}
      </button>
    ))}
  </div>
)

export const SearchBox: React.FC<{
  value: string
  onChange: (v: string) => void
  placeholder?: string
}> = ({ value, onChange, placeholder = "Search..." }) => (
  <div className="relative">
    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
    />
  </div>
)

/** White bordered secondary button - the "Filters" button on Billing. */
export const OutlineButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ children, ...props }) => (
  <button
    type="button"
    {...props}
    className={`px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors text-sm ${
      props.className || ""
    }`}
  >
    {children}
  </button>
)

export const RefreshButton: React.FC<{ loading?: boolean; onClick: () => void }> = ({ loading, onClick }) => (
  <OutlineButton onClick={onClick} disabled={loading} title="Refresh">
    <FaSyncAlt className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
  </OutlineButton>
)

/** The green "Export Excel" button from Billing / Project Sites. */
export const ExportButton: React.FC<{ onClick: () => void; busy?: boolean; label?: string }> = ({
  onClick,
  busy,
  label = "Export Excel",
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
  >
    <FaFileExcel className="w-4 h-4" />
    {busy ? "Preparing..." : label}
  </button>
)

/**
 * The table inside a ListCard. Loading and empty states render as a row under
 * the headings, as they do on Billing, so the columns never jump.
 */
export const DataTable: React.FC<{
  head: React.ReactNode
  colSpan: number
  loading: boolean
  empty: boolean
  loadingText?: string
  emptyText?: string
  emptyHint?: React.ReactNode
  emptyIcon?: React.ReactNode
  foot?: React.ReactNode
  children?: React.ReactNode
}> = ({
  head,
  colSpan,
  loading,
  empty,
  loadingText = "Loading...",
  emptyText = "Nothing to show",
  emptyHint,
  emptyIcon,
  foot,
  children,
}) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>{head}</tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {loading ? (
          <tr>
            <td colSpan={colSpan} className="px-4 sm:px-6 py-12 text-center">
              <div className="flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3" />
                <span className="text-sm text-gray-600">{loadingText}</span>
              </div>
            </td>
          </tr>
        ) : empty ? (
          <tr>
            <td colSpan={colSpan} className="px-4 sm:px-6 py-12 text-center">
              <div className="flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3 text-2xl text-gray-400">
                  {emptyIcon}
                </div>
                <p className="text-sm text-gray-500">{emptyText}</p>
                {emptyHint ? <div className="text-xs text-gray-400 mt-1 max-w-md">{emptyHint}</div> : null}
              </div>
            </td>
          </tr>
        ) : (
          children
        )}
      </tbody>
      {!loading && !empty ? foot : null}
    </table>
  </div>
)

/** Column heading - list tables, detail tables and form line tables alike. */
export const Th: React.FC<{ children?: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <th
    className={`px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider whitespace-nowrap ${className}`}
  >
    {children}
  </th>
)

/** A row cell in a ListCard table (the `px-4 py-4` cell of Billing). */
export const Cell: React.FC<{ children?: React.ReactNode; className?: string; wrap?: boolean }> = ({
  children,
  className = "",
  wrap,
}) => <td className={`px-4 py-4 text-sm text-gray-900 ${wrap ? "" : "whitespace-nowrap"} ${className}`}>{children}</td>

/** A compact cell for the nested detail tables and the form line tables. */
export const Td: React.FC<{ children?: React.ReactNode; className?: string; colSpan?: number }> = ({
  children,
  className = "",
  colSpan,
}) => (
  <td colSpan={colSpan} className={`px-4 py-3 text-sm text-gray-800 ${className}`}>
    {children}
  </td>
)

/** The expanded row under a list row - line items, remarks, the receipt screen. */
export const DetailRow: React.FC<{ colSpan: number; children: React.ReactNode }> = ({ colSpan, children }) => (
  <tr className="bg-gray-50">
    <td colSpan={colSpan} className="px-4 sm:px-6 py-3">
      {children}
    </td>
  </tr>
)

const ICON_TONE = {
  blue: "text-blue-600 hover:text-blue-800 hover:bg-blue-50",
  indigo: "text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50",
  green: "text-green-600 hover:text-green-800 hover:bg-green-50",
  teal: "text-teal-600 hover:text-teal-800 hover:bg-teal-50",
  emerald: "text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50",
  purple: "text-purple-600 hover:text-purple-800 hover:bg-purple-50",
  amber: "text-amber-600 hover:text-amber-800 hover:bg-amber-50",
  red: "text-red-600 hover:text-red-800 hover:bg-red-50",
  gray: "text-gray-600 hover:text-gray-800 hover:bg-gray-100",
} as const

/**
 * An icon button in the Actions column, as on Billing and Project Sites. The
 * title is the only label the action has, so it is required.
 */
export const IconAction: React.FC<{
  title: string
  onClick: () => void
  tone?: keyof typeof ICON_TONE
  disabled?: boolean
  children: React.ReactNode
}> = ({ title, onClick, tone = "blue", disabled, children }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    disabled={disabled}
    className={`${ICON_TONE[tone]} p-2 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed [&>svg]:w-4 [&>svg]:h-4`}
  >
    {children}
  </button>
)

/** The first icon in every Actions cell: show / hide the row's detail. */
export const ExpandAction: React.FC<{ open: boolean; onToggle: () => void; what?: string }> = ({
  open,
  onToggle,
  what = "details",
}) => (
  <IconAction title={open ? `Hide ${what}` : `Show ${what}`} onClick={onToggle}>
    {open ? <LuEyeOff /> : <LuEye />}
  </IconAction>
)

/**
 * A small, honest note when a scoped list comes back empty.
 *
 * "No records" and "you are on no projects" look identical to a user, and the
 * second one is a support ticket if nobody says so.
 */
export const ScopeNote: React.FC<{ className?: string }> = ({ className = "" }) => (
  <p className={`text-xs text-gray-500 ${className}`}>
    Material Management is scoped to the projects you are assigned to. If a site, warehouse or person you expect is
    missing, you are not on that project.
  </p>
)

export const Field: React.FC<{ label: string; children: React.ReactNode; required?: boolean; hint?: string }> = ({
  label,
  children,
  required,
  hint,
}) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">
      {label} {required ? <span className="text-red-500">*</span> : null}
    </label>
    {children}
    {hint ? <p className="text-[11px] text-gray-500 mt-1">{hint}</p> : null}
  </div>
)

export const TextInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input
    {...props}
    className={`w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 ${
      props.className || ""
    }`}
  />
)

export const TextArea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = (props) => (
  <textarea
    {...props}
    className={`w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 ${
      props.className || ""
    }`}
  />
)

export const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = (props) => (
  <select
    {...props}
    className={`w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-800 ${
      props.className || ""
    }`}
  />
)

export const PrimaryButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean }
> = ({ loading, children, ...props }) => (
  <button
    {...props}
    disabled={props.disabled || loading}
    className={`inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-950 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
      props.className || ""
    }`}
  >
    {loading ? <FaSpinner className="animate-spin" /> : null}
    {children}
  </button>
)

export const GhostButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ children, ...props }) => (
  <button
    {...props}
    className={`inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 ${
      props.className || ""
    }`}
  >
    {children}
  </button>
)

/** A compact stat tile for the group-heads dashboard. */
export const StatTile: React.FC<{ label: string; value: string; hint?: string; tone?: string }> = ({
  label,
  value,
  hint,
  tone = "text-gray-900",
}) => (
  <div className="bg-white rounded border border-gray-200 p-4">
    <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
    <p className={`text-xl font-semibold mt-1 ${tone}`}>{value}</p>
    {hint ? <p className="text-[11px] text-gray-400 mt-1">{hint}</p> : null}
  </div>
)

/** Holder type + id, picked together. The two are meaningless apart. */
export const HolderPicker: React.FC<{
  holderType: HolderType
  onHolderTypeChange: (t: HolderType) => void
  value: FilterOption | null
  onChange: (o: FilterOption | null) => void
  fetchers: Record<HolderType, (search: string) => Promise<FilterOption[]>>
  allowed?: HolderType[]
  dependency?: any
  disabled?: boolean
}> = ({ holderType, onHolderTypeChange, value, onChange, fetchers, allowed, dependency, disabled }) => {
  const types = allowed || (["warehouse", "staff", "site"] as HolderType[])
  const fetcher = useMemo(() => fetchers[holderType], [fetchers, holderType])
  return (
    <div className="flex gap-2">
      <Select
        value={holderType}
        disabled={disabled}
        onChange={(e) => {
          onHolderTypeChange(e.target.value as HolderType)
          onChange(null)
        }}
        className="!w-36"
      >
        {types.map((t) => (
          <option key={t} value={t}>
            {HOLDER_LABELS[t]}
          </option>
        ))}
      </Select>
      <AsyncPicker
        className="flex-1"
        value={value}
        onChange={onChange}
        fetcher={fetcher}
        disabled={disabled}
        dependency={`${holderType}:${dependency ?? ""}`}
        placeholder={`Select ${HOLDER_LABELS[holderType].toLowerCase()}...`}
      />
    </div>
  )
}
